import React, { useState, useEffect } from 'react';
import { api, formatCurrency, formatDate, formatDateTime } from '../lib/utils';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Settings as SettingsIcon,
  Euro,
  DollarSign,
  Download,
  Upload,
  Shield,
  AlertTriangle,
  Package,
  FileText,
  RefreshCw,
  Printer,
  Phone,
  Store,
  Palette,
  Eye
} from 'lucide-react';
import { useStore } from '../context/StoreContext';

export default function Settings() {
  const [loading, setLoading] = useState(true);
  const { currentStore, stores, refreshStores } = useStore();
  const [storeSettings, setStoreSettings] = useState(null);
  const [warrantySettings, setWarrantySettings] = useState({
    warranty_primary_color: '#6D28D9',
    warranty_secondary_color: '#84CC16',
    warranty_font_family: 'Arial',
    warranty_show_logo: true,
    warranty_show_terms: true,
    warranty_covered_terms: 'Garancia mbulon defektet e prodhimit\nRiparimi kryhet brenda 14 ditëve pune\nZëvendësimi bëhet vetëm nëse riparimi është i pamundur',
    warranty_excluded_terms: 'Nuk mbulon dëmtimet fizike\nNuk mbulon dëmtimet nga uji\nNuk mbulon keqpërdorimin',
    warranty_footer_text: 'Ruajeni këtë kartë dhe faturën origjinale'
  });
  const [showPreview, setShowPreview] = useState(false);
  const [businessSettings, setBusinessSettings] = useState({
    business_name: 'Tech Store',
    nipt: '',
    address: '',
    phone: '',
    email: '',
    logo_url: '',
    default_warranty_months: 12,
    currency_symbol: 'LEK',
    invoice_template: 'default',
    invoice_header: '',
    invoice_footer: 'Faleminderit per blerjen!',
    warranty_template: 'default',
    warranty_terms: 'Garancia mbulon defektet e prodhimit. Nuk mbulon demtime fizike ose nga uji.'
  });
  const [exchangeRates, setExchangeRates] = useState({ EUR: 10, USD: 9.5 });
  const [expiringWarranties, setExpiringWarranties] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [dailyReport, setDailyReport] = useState(null);
  const [reportDate, setReportDate] = useState(new Date().toISOString().split('T')[0]);
  const [isBackupModalOpen, setIsBackupModalOpen] = useState(false);
  const [backupFile, setBackupFile] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    if (currentStore) {
      loadData();
    }
  }, [currentStore]);

  // Load store settings when currentStore changes
  useEffect(() => {
    if (currentStore) {
      setStoreSettings({
        ...currentStore, // Preserve all other store fields (type, modules, etc.)
        id: currentStore.id,
        name: currentStore.name,
        address: currentStore.address || '',
        phone: currentStore.phone || '',
        email: currentStore.email || '',
        nipt: currentStore.nipt || '',
        logo_url: currentStore.logo_url || '',
        invoice_header: currentStore.invoice_header || '',
        invoice_footer: currentStore.invoice_footer || 'Faleminderit per blerjen!',
        warranty_terms: currentStore.warranty_terms || '',
        invoice_show_logo: currentStore.invoice_show_logo !== false,
        invoice_show_nipt: currentStore.invoice_show_nipt !== false,
        invoice_show_address: currentStore.invoice_show_address !== false,
        invoice_show_phone: currentStore.invoice_show_phone !== false,
        invoice_primary_color: currentStore.invoice_primary_color || '#E52B50',
        invoice_secondary_color: currentStore.invoice_secondary_color || '#1a1a2e',
        invoice_font_family: currentStore.invoice_font_family || 'Arial',
        receipt_width: currentStore.receipt_width || '80mm'
      });
    }
  }, [currentStore]);

  const saveStoreInvoiceSettings = async () => {
    if (!storeSettings) return;
    try {
      await api.put(`/stores/${storeSettings.id}`, storeSettings);
      toast.success('Cilësimet e faturës u ruajtën');
      refreshStores();
    } catch (error) {
      toast.error('Gabim në ruajtjen e cilësimeve');
    }
  };

  // Load warranty settings from currentStore
  useEffect(() => {
    if (currentStore) {
      setWarrantySettings({
        warranty_primary_color: currentStore.warranty_primary_color || '#6D28D9',
        warranty_secondary_color: currentStore.warranty_secondary_color || '#84CC16',
        warranty_font_family: currentStore.warranty_font_family || 'Arial',
        warranty_show_logo: currentStore.warranty_show_logo !== false,
        warranty_show_terms: currentStore.warranty_show_terms !== false,
        warranty_covered_terms: currentStore.warranty_covered_terms || 'Garancia mbulon defektet e prodhimit\nRiparimi kryhet brenda 14 ditëve pune\nZëvendësimi bëhet vetëm nëse riparimi është i pamundur',
        warranty_excluded_terms: currentStore.warranty_excluded_terms || 'Nuk mbulon dëmtimet fizike\nNuk mbulon dëmtimet nga uji\nNuk mbulon keqpërdorimin',
        warranty_footer_text: currentStore.warranty_footer_text || 'Ruajeni këtë kartë dhe faturën origjinale'
      });
    }
  }, [currentStore]);

  const saveWarrantySettings = async () => {
    if (!currentStore?.id) return;
    try {
      const updateData = {
        ...currentStore,
        ...warrantySettings,
        id: currentStore.id
      };
      await api.put(`/stores/${currentStore.id}`, updateData);
      toast.success('Cilësimet e garancisë u ruajtën');
      refreshStores();
    } catch (error) {
      console.error(error);
      toast.error('Gabim në ruajtjen e cilësimeve');
    }
  };

  const loadData = async () => {
    try {
      if (!currentStore?.id) return;
      const [settingsRes, ratesRes, warrantiesRes, lowStockRes] = await Promise.all([
        api.get(`/settings/business?store_id=${currentStore.id}`),
        api.get(`/exchange-rates?store_id=${currentStore.id}`),
        api.get(`/warranties/expiring?days=30&store_id=${currentStore.id}`),
        api.get(`/products/low-stock?store_id=${currentStore.id}`)
      ]);
      setBusinessSettings(settingsRes.data);
      setExchangeRates(ratesRes.data);
      setExpiringWarranties(warrantiesRes.data);
      setLowStockProducts(lowStockRes.data);
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDailyReport = async () => {
    try {
      if (!currentStore?.id) return;
      const response = await api.get(`/reports/daily?date=${reportDate}&store_id=${currentStore.id}`);
      setDailyReport(response.data);
    } catch (error) {
      toast.error('Gabim në ngarkimin e raportit');
    }
  };

  const saveBusinessSettings = async () => {
    try {
      if (!currentStore?.id) return;
      await api.put(`/settings/business?store_id=${currentStore.id}`, businessSettings);
      toast.success('Cilësimet u ruajtën');
    } catch (error) {
      toast.error('Gabim në ruajtjen e cilësimeve');
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingLogo(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/upload-logo`, {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      setBusinessSettings(prev => ({ ...prev, logo_url: data.logo_url }));
      toast.success('Logo u ngarkua');
    } catch (error) {
      toast.error('Gabim në ngarkimin e logos');
    } finally {
      setUploadingLogo(false);
    }
  };

  const saveExchangeRates = async () => {
    try {
      if (!currentStore?.id) return;
      await api.put(`/exchange-rates?eur=${exchangeRates.EUR}&usd=${exchangeRates.USD}&store_id=${currentStore.id}`);
      toast.success('Kurset u përditësuan');
    } catch (error) {
      toast.error('Gabim në përditësimin e kurseve');
    }
  };

  const exportBackup = async () => {
    try {
      if (!currentStore?.id) return;
      const response = await api.get(`/backup/export?store_id=${currentStore.id}`);
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = window.URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `techstore_backup_${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Backup u eksportua');
    } catch (error) {
      toast.error('Gabim në eksportimin e backup');
    }
  };

  const importBackup = async () => {
    if (!backupFile) {
      toast.error('Zgjidhni skedarin e backup');
      return;
    }

    try {
      const text = await backupFile.text();
      const data = JSON.parse(text);
      if (!currentStore?.id) return;

      await api.post(`/backup/import?store_id=${currentStore.id}`, { data });
      toast.success('Backup u importua me sukses');
      setIsBackupModalOpen(false);
      loadData();
    } catch (error) {
      toast.error('Gabim në importimin e backup');
    }
  };

  const printDailyReport = () => {
    if (!dailyReport) return;

    const printWindow = window.open('', '_blank', 'width=800,height=600');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Raport Ditor - ${dailyReport.date}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { text-align: center; color: #6D28D9; }
            .header { text-align: center; margin-bottom: 30px; }
            .section { margin: 20px 0; padding: 15px; border: 1px solid #ddd; border-radius: 8px; }
            .section h3 { margin-top: 0; color: #333; border-bottom: 1px solid #eee; padding-bottom: 10px; }
            .row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f0f0f0; }
            .row:last-child { border-bottom: none; }
            .label { color: #666; }
            .value { font-weight: bold; font-family: monospace; }
            .highlight { background: #f0fdf4; }
            .warning { background: #fef3c7; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th, td { padding: 8px; text-align: left; border-bottom: 1px solid #eee; }
            th { background: #f5f5f5; }
            .footer { text-align: center; margin-top: 30px; color: #888; font-size: 12px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>📊 RAPORT DITOR</h1>
            <p><strong>${businessSettings.business_name}</strong></p>
            <p>Data: ${formatDate(dailyReport.date)}</p>
          </div>
          
          <div class="section highlight">
            <h3>💰 Përmbledhje Financiare</h3>
            <div class="row">
              <span class="label">Shitje Totale:</span>
              <span class="value">${formatCurrency(dailyReport.summary.total_sales)}</span>
            </div>
            <div class="row">
              <span class="label">Para të Arkëtuara:</span>
              <span class="value">${formatCurrency(dailyReport.summary.total_cash_received)}</span>
            </div>
            <div class="row">
              <span class="label">Borxhe të Reja:</span>
              <span class="value">${formatCurrency(dailyReport.summary.total_debt_created)}</span>
            </div>
            <div class="row">
              <span class="label">Pagesa Borxhesh:</span>
              <span class="value">${formatCurrency(dailyReport.summary.total_debt_payments)}</span>
            </div>
            <div class="row">
              <span class="label">Blerje:</span>
              <span class="value">${formatCurrency(dailyReport.summary.total_purchases)}</span>
            </div>
            <div class="row" style="font-size: 18px; padding-top: 15px; border-top: 2px solid #6D28D9;">
              <span class="label">💵 Fluksi Neto i Parave:</span>
              <span class="value" style="color: ${dailyReport.summary.net_cash_flow >= 0 ? '#16a34a' : '#dc2626'}">
                ${formatCurrency(dailyReport.summary.net_cash_flow)}
              </span>
            </div>
          </div>
          
          <div class="section">
            <h3>💳 Mënyra e Pagesës</h3>
            <div class="row">
              <span class="label">Kesh:</span>
              <span class="value">${formatCurrency(dailyReport.payment_breakdown.cash)}</span>
            </div>
            <div class="row">
              <span class="label">Kartë:</span>
              <span class="value">${formatCurrency(dailyReport.payment_breakdown.card)}</span>
            </div>
          </div>
          
          <div class="section">
            <h3>📈 Numërime</h3>
            <div class="row">
              <span class="label">Shitje:</span>
              <span class="value">${dailyReport.counts.sales_count}</span>
            </div>
            <div class="row">
              <span class="label">Blerje:</span>
              <span class="value">${dailyReport.counts.purchases_count}</span>
            </div>
            <div class="row">
              <span class="label">Servise të Përfunduara:</span>
              <span class="value">${dailyReport.counts.services_completed}</span>
            </div>
          </div>
          
          ${dailyReport.top_products.length > 0 ? `
            <div class="section">
              <h3>🏆 Produktet Më të Shitura</h3>
              <table>
                <thead>
                  <tr><th>Produkti</th><th>Sasia</th><th>Totali</th></tr>
                </thead>
                <tbody>
                  ${dailyReport.top_products.map(p => `
                    <tr>
                      <td>${p.name}</td>
                      <td>${p.quantity}</td>
                      <td style="font-family:monospace;">${formatCurrency(p.total)}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          ` : ''}
          
          <div class="footer">
            <p>Raport i gjeneruar më: ${formatDateTime(dailyReport.generated_at)}</p>
            <p>${businessSettings.business_name} | ${businessSettings.phone}</p>
          </div>
          
          <script>window.onload = function() { window.print(); }</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      <Tabs defaultValue="invoice" className="space-y-6">
        <TabsList className="grid grid-cols-6 w-full max-w-4xl">
          <TabsTrigger value="invoice" data-testid="tab-invoice">Fatura</TabsTrigger>
          {currentStore?.type !== 'boutique' && (
            <TabsTrigger value="warranty" data-testid="tab-warranty">Garanci</TabsTrigger>
          )}
          <TabsTrigger value="rates" data-testid="tab-rates">Kurset</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts">Njoftime</TabsTrigger>
          <TabsTrigger value="daily-reports" data-testid="tab-daily-reports">Raport</TabsTrigger>
          <TabsTrigger value="backup-restore" data-testid="tab-backup">Backup</TabsTrigger>
        </TabsList>

        {/* Invoice Template Settings */}
        <TabsContent value="invoice" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Store className="w-5 h-5" />
                Template Fature për Dyqan
              </CardTitle>
              <CardDescription>Personalizo pamjen e faturave për secilin dyqan</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Store Selector Removed */}

              {storeSettings && (
                <>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Simplified Invoice Settings Layout */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Left Column - Settings */}
                      <div className="space-y-6">

                        {/* Visual Style Section */}
                        <div className="bg-slate-50 p-4 rounded-lg border">
                          <h3 className="font-semibold text-sm mb-3 border-b pb-2">🎨 Dizajni dhe Stili</h3>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-xs text-slate-600">Ngjyra e Brendit</Label>
                              <div className="flex gap-2 mt-1">
                                <Input
                                  type="color"
                                  value={storeSettings.invoice_primary_color}
                                  onChange={(e) => setStoreSettings({ ...storeSettings, invoice_primary_color: e.target.value })}
                                  className="w-12 h-9 p-1 cursor-pointer"
                                />
                                <Input
                                  value={storeSettings.invoice_primary_color}
                                  onChange={(e) => setStoreSettings({ ...storeSettings, invoice_primary_color: e.target.value })}
                                  className="font-mono text-sm h-9"
                                />
                              </div>
                            </div>

                            <div>
                              <Label className="text-xs text-slate-600">Fonti i Faturës</Label>
                              <Select
                                value={storeSettings.invoice_font_family}
                                onValueChange={(v) => setStoreSettings({ ...storeSettings, invoice_font_family: v })}
                              >
                                <SelectTrigger className="h-9 mt-1">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Arial">Arial (Standard)</SelectItem>
                                  <SelectItem value="Courier New">Courier New (Mono)</SelectItem>
                                  <SelectItem value="Helvetica">Helvetica (Modern)</SelectItem>
                                  <SelectItem value="Times New Roman">Times New Roman</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          <div className="mt-4">
                            <Label className="text-xs text-slate-600">Gjerësia e Letrës (Thermal)</Label>
                            <div className="flex gap-2 mt-1">
                              {['58mm', '80mm', 'A4'].map(size => (
                                <div
                                  key={size}
                                  onClick={() => setStoreSettings({ ...storeSettings, receipt_width: size })}
                                  className={`
                                        cursor-pointer px-3 py-1.5 rounded text-xs font-medium border transition-colors
                                        ${storeSettings.receipt_width === size
                                      ? 'bg-primary text-primary-foreground border-primary'
                                      : 'bg-white text-slate-600 hover:bg-slate-100'}
                                      `}
                                >
                                  {size === '58mm' ? 'Small (58mm)' : size === '80mm' ? 'Standard (80mm)' : 'Full Page (A4)'}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Content Section */}
                        <div className="bg-slate-50 p-4 rounded-lg border">
                          <h3 className="font-semibold text-sm mb-3 border-b pb-2">📄 Përmbajtja e Faturës</h3>

                          <div className="space-y-3 mb-4">
                            <div>
                              <Label className="text-xs text-slate-600">Mesazh në Krye (Header)</Label>
                              <Textarea
                                value={storeSettings.invoice_header}
                                onChange={(e) => setStoreSettings({ ...storeSettings, invoice_header: e.target.value })}
                                placeholder="E.g. Mirësevini në dyqanin tonë!"
                                rows={2}
                                className="mt-1 mb-4"
                              />
                            </div>

                            {[
                              { id: 'logo', label: 'Shfaq Logon', key: 'invoice_show_logo' },
                              { id: 'nipt', label: 'Shfaq NIPT-in', key: 'invoice_show_nipt' },
                              { id: 'addr', label: 'Shfaq Adresën', key: 'invoice_show_address' },
                              { id: 'phone', label: 'Shfaq Telefonin', key: 'invoice_show_phone' },
                            ].map(item => (
                              <div key={item.id} className="flex items-center justify-between">
                                <Label htmlFor={`switch-${item.id}`} className="text-sm cursor-pointer">{item.label}</Label>
                                <Switch
                                  id={`switch-${item.id}`}
                                  checked={storeSettings[item.key]}
                                  onCheckedChange={(v) => setStoreSettings({ ...storeSettings, [item.key]: v })}
                                />
                              </div>
                            ))}
                          </div>

                          <div>
                            <Label className="text-xs text-slate-600">Mesazh në Fund (Footer)</Label>
                            <Textarea
                              value={storeSettings.invoice_footer}
                              onChange={(e) => setStoreSettings({ ...storeSettings, invoice_footer: e.target.value })}
                              placeholder="Faleminderit që zgjodhët dyqanin tonë!"
                              rows={2}
                              className="mt-1"
                            />
                          </div>
                        </div>

                      </div>

                      {/* Right Column - Preview */}
                      <div>
                        <h3 className="font-semibold flex items-center gap-2 mb-4">
                          <Eye className="w-4 h-4" />
                          Paraqitja e Faturës
                        </h3>
                        <div className="flex justify-center bg-slate-100 p-6 rounded-xl border border-dashed min-h-[400px] items-start overflow-hidden">
                          <div
                            className="bg-white shadow-lg p-4 transition-all duration-300 origin-top"
                            style={{
                              fontFamily: storeSettings.invoice_font_family,
                              width: storeSettings.receipt_width === '58mm' ? '220px' : storeSettings.receipt_width === '80mm' ? '300px' : '400px',
                              minHeight: '350px'
                            }}
                          >
                            {/* Invoice Preview Content */}
                            <div className="text-center border-b pb-4 mb-4" style={{ borderColor: storeSettings.invoice_primary_color }}>
                              {storeSettings.invoice_show_logo && storeSettings.logo_url && (
                                <img src={storeSettings.logo_url} alt="Logo" className="h-10 mx-auto mb-2 object-contain" />
                              )}
                              <h2 className="font-bold text-lg uppercase leading-tight" style={{ color: storeSettings.invoice_primary_color }}>
                                {storeSettings.name}
                              </h2>
                              <div className="text-[10px] text-slate-500 mt-1 space-y-0.5">
                                {storeSettings.invoice_show_address && storeSettings.address && <p>{storeSettings.address}</p>}
                                {storeSettings.invoice_show_phone && storeSettings.phone && <p>Tel: {storeSettings.phone}</p>}
                                {storeSettings.invoice_show_nipt && storeSettings.nipt && <p>NIPT: {storeSettings.nipt}</p>}
                                {storeSettings.invoice_header && <p className="mt-2 font-medium italic">{storeSettings.invoice_header}</p>}
                              </div>
                            </div>

                            <div className="text-[10px] space-y-1 mb-4 border-b pb-2 border-dashed">
                              <div className="flex justify-between">
                                <span className="text-slate-500">Data / Koha:</span>
                                <span className="font-mono">{formatDateTime(new Date().toISOString())}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">Nr. Faturës:</span>
                                <span className="font-mono">#INV-2024-001</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">Klienti:</span>
                                <span>Klient i Rastit</span>
                              </div>
                            </div>

                            <table className="w-full text-[11px] mb-4">
                              <thead>
                                <tr className="border-b" style={{ borderColor: storeSettings.invoice_primary_color }}>
                                  <th className="text-left py-1 font-semibold">Artikulli</th>
                                  <th className="text-center w-8">Sa.</th>
                                  <th className="text-right w-16">Vlera</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-dashed">
                                <tr>
                                  <td className="py-2">iPhone 15 Pro Max</td>
                                  <td className="text-center">1</td>
                                  <td className="text-right font-mono">145,000</td>
                                </tr>
                                <tr>
                                  <td className="py-2">Këllëf Mbrojtës Silikoni</td>
                                  <td className="text-center">2</td>
                                  <td className="text-right font-mono">3,000</td>
                                </tr>
                                <tr>
                                  <td className="py-2">Xham Mbrojtës</td>
                                  <td className="text-center">1</td>
                                  <td className="text-right font-mono">1,500</td>
                                </tr>
                              </tbody>
                            </table>

                            <div className="border-t-2 pt-2 space-y-1" style={{ borderColor: storeSettings.invoice_primary_color }}>
                              <div className="flex justify-between text-xs">
                                <span>Nëntotali:</span>
                                <span className="font-mono">149,500</span>
                              </div>
                              <div className="flex justify-between text-base font-bold" style={{ color: storeSettings.invoice_primary_color }}>
                                <span>TOTALI:</span>
                                <span className="font-mono">149,500 LEK</span>
                              </div>
                            </div>

                            {storeSettings.invoice_footer && (
                              <div className="mt-6 pt-4 border-t border-dashed text-center text-[10px] text-slate-500 italic">
                                {storeSettings.invoice_footer}
                              </div>
                            )}

                            <div className="mt-4 text-[9px] text-center text-slate-400">
                              Powered by MiriShop
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-4 border-t">
                    <Button onClick={saveStoreInvoiceSettings} data-testid="save-invoice-settings">
                      <FileText className="w-4 h-4 mr-2" />
                      Ruaj Cilësimet
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Warranty Template Settings */}
        {currentStore?.type !== 'boutique' && (
          <TabsContent value="warranty" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Template Garancie për Dyqan
                </CardTitle>
                <CardDescription>Personalizo pamjen e kartës së garancisë për secilin dyqan</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Store Selector Removed */}

                {currentStore?.id && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Simplified Warranty Settings Layout */}
                    <div className="space-y-6">

                      <div className="bg-slate-50 p-4 rounded-lg border">
                        <h3 className="font-semibold text-sm mb-3">🎨 Stili Vizual</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Ngjyra e Brendit</Label>
                            <div className="flex gap-2 mt-1.5">
                              <Input
                                type="color"
                                value={warrantySettings.warranty_primary_color}
                                onChange={(e) => setWarrantySettings({ ...warrantySettings, warranty_primary_color: e.target.value })}
                                className="w-12 h-10 p-1 cursor-pointer"
                              />
                              <Input
                                value={warrantySettings.warranty_primary_color}
                                onChange={(e) => setWarrantySettings({ ...warrantySettings, warranty_primary_color: e.target.value })}
                                className="font-mono text-sm"
                              />
                            </div>
                          </div>
                          <div>
                            <Label>Fonti i Dokumentit</Label>
                            <Select
                              value={warrantySettings.warranty_font_family}
                              onValueChange={(v) => setWarrantySettings({ ...warrantySettings, warranty_font_family: v })}
                            >
                              <SelectTrigger className="mt-1.5">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Arial">Arial (Standard)</SelectItem>
                                <SelectItem value="Helvetica">Helvetica (Modern)</SelectItem>
                                <SelectItem value="Georgia">Georgia (Formal)</SelectItem>
                                <SelectItem value="Times New Roman">Times New Roman (Classic)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="flex items-center justify-between mt-4">
                          <Label className="cursor-pointer" htmlFor="show-logo-switch">Shfaq Logon e Dyqanit në Garanci</Label>
                          <Switch
                            id="show-logo-switch"
                            checked={warrantySettings.warranty_show_logo}
                            onCheckedChange={(v) => setWarrantySettings({ ...warrantySettings, warranty_show_logo: v })}
                          />
                        </div>
                      </div>

                      <div className="bg-slate-50 p-4 rounded-lg border">
                        <h3 className="font-semibold text-sm mb-3">📝 Kushtet e Përgjithshme (Default)</h3>
                        <p className="text-xs text-slate-500 mb-4">Këto kushte do të përdoren kur një shabllon specifik nuk ka kushte të definuara.</p>

                        <div className="space-y-4">
                          <div>
                            <Label className="text-xs text-slate-600">Kushtet e Mbulimit</Label>
                            <Textarea
                              value={warrantySettings.warranty_covered_terms}
                              onChange={(e) => setWarrantySettings({ ...warrantySettings, warranty_covered_terms: e.target.value })}
                              rows={3}
                              className="mt-1"
                              placeholder="p.sh. Defektet e fabrikës..."
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-600">Përjashtimet</Label>
                            <Textarea
                              value={warrantySettings.warranty_excluded_terms}
                              onChange={(e) => setWarrantySettings({ ...warrantySettings, warranty_excluded_terms: e.target.value })}
                              rows={3}
                              className="mt-1"
                              placeholder="p.sh. Dëmtimet fizike, uji..."
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-600">Shënimi në Fund</Label>
                            <Input
                              value={warrantySettings.warranty_footer_text}
                              onChange={(e) => setWarrantySettings({ ...warrantySettings, warranty_footer_text: e.target.value })}
                              className="mt-1"
                              placeholder="Ruajeni këtë kartë..."
                            />
                          </div>
                        </div>
                      </div>

                      <div className="pt-2">
                        <h3 className="font-semibold mb-3 flex items-center gap-2 text-sm">
                          <FileText className="w-4 h-4 text-slate-500" />
                          Shabllonet Aktive
                        </h3>
                        {(() => {
                          const templates = currentStore?.warranty_templates || [];

                          if (templates.length === 0) {
                            return <div className="text-center p-6 border border-dashed rounded-lg bg-slate-50 text-slate-400 text-sm">Nuk ka shabllone të personalizuara</div>;
                          }

                          return (
                            <div className="grid grid-cols-2 gap-3">
                              {templates.map((t, idx) => (
                                <div key={idx} className="p-2 border rounded bg-white text-xs flex justify-between items-center">
                                  <span className="font-medium truncate mr-2">{t.category}</span>
                                  <Badge variant="secondary" className="text-[10px] h-5 px-1">{t.duration_months}m</Badge>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                        <Button variant="link" className="px-0 text-xs h-auto mt-2 text-primary" onClick={() => document.getElementById('tab-sales')?.click()}>
                          Menaxho Shabllonet tek Klientet &rarr;
                        </Button>
                      </div>

                    </div>

                    {/* Right Column - Preview */}
                    <div>
                      <h3 className="font-semibold flex items-center gap-2 mb-4">
                        <Eye className="w-4 h-4" />
                        Paraqitja e Kartës
                      </h3>
                      <div
                        className="border-2 rounded-xl p-5 bg-white shadow-sm max-w-sm mx-auto"
                        style={{
                          borderColor: warrantySettings.warranty_primary_color,
                          fontFamily: warrantySettings.warranty_font_family
                        }}
                      >
                        <div className="text-center border-b-2 pb-4 mb-4" style={{ borderColor: warrantySettings.warranty_primary_color }}>
                          {warrantySettings.warranty_show_logo && storeSettings?.logo_url && (
                            <img src={storeSettings.logo_url} alt="Logo" className="h-10 mx-auto mb-2" />
                          )}
                          <h2 className="text-xl font-bold" style={{ color: warrantySettings.warranty_primary_color }}>
                            KARTË GARANCIE
                          </h2>
                          <p className="text-sm text-slate-500">{storeSettings?.name || 'DYQANI'}</p>
                        </div>

                        <div className="space-y-2 text-sm mb-4">
                          <div className="flex justify-between border-b pb-1">
                            <span className="text-slate-500">Nr. Garancie:</span>
                            <span className="font-bold">ABC12345</span>
                          </div>
                          <div className="flex justify-between border-b pb-1">
                            <span className="text-slate-500">Klienti:</span>
                            <span className="font-bold">Klient Shembull</span>
                          </div>
                          <div className="flex justify-between border-b pb-1">
                            <span className="text-slate-500">Produkti:</span>
                            <span className="font-bold">iPhone 15 Pro</span>
                          </div>
                        </div>

                        <div
                          className="rounded-lg p-4 text-center text-white mb-4"
                          style={{ backgroundColor: warrantySettings.warranty_primary_color }}
                        >
                          <h3 className="text-2xl font-bold">12 MUAJ</h3>
                          <p className="text-sm opacity-90">Deri më: 08.01.2027</p>
                        </div>

                        {warrantySettings.warranty_show_terms && (
                          <div className="text-xs bg-slate-50 rounded-lg p-3 mb-3">
                            <p className="font-semibold mb-2" style={{ color: warrantySettings.warranty_primary_color }}>Mbulohet:</p>
                            <ul className="list-disc list-inside text-slate-600 mb-2">
                              {warrantySettings.warranty_covered_terms.split('\n').slice(0, 3).map((t, i) => (
                                <li key={i}>{t}</li>
                              ))}
                            </ul>
                            <p className="font-semibold mb-2 text-slate-700">Nuk mbulohet:</p>
                            <ul className="list-disc list-inside text-slate-600">
                              {warrantySettings.warranty_excluded_terms.split('\n').slice(0, 3).map((t, i) => (
                                <li key={i}>{t}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        <p className="text-center text-xs text-slate-500">{warrantySettings.warranty_footer_text}</p>
                      </div>
                    </div>
                  </div>
                )}

                {currentStore?.id && (
                  <div className="flex gap-2 pt-4 border-t">
                    <Button onClick={saveWarrantySettings} data-testid="save-warranty-settings">
                      <Shield className="w-4 h-4 mr-2" />
                      Ruaj Cilësimet e Garancisë
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Exchange Rates */}
        <TabsContent value="rates">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Euro className="w-5 h-5" />
                Kurset e Këmbimit
              </CardTitle>
              <CardDescription>Përditëso kurset e valutave të huaja</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-md">
                <div>
                  <Label className="flex items-center gap-2">
                    <Euro className="w-4 h-4" />
                    1 EUR = ? LEK
                  </Label>
                  <Input
                    type="number"
                    value={exchangeRates.EUR}
                    onChange={(e) => setExchangeRates({ ...exchangeRates, EUR: parseFloat(e.target.value) || 0 })}
                    className="font-mono"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    1 USD = ? LEK
                  </Label>
                  <Input
                    type="number"
                    value={exchangeRates.USD}
                    onChange={(e) => setExchangeRates({ ...exchangeRates, USD: parseFloat(e.target.value) || 0 })}
                    className="font-mono"
                  />
                </div>
              </div>
              <p className="text-sm text-slate-500">
                Përditësuar: {exchangeRates.updated_at ? formatDateTime(exchangeRates.updated_at) : 'Default'}
              </p>
              <Button onClick={saveExchangeRates}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Përditëso Kurset
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Alerts */}
        <TabsContent value="alerts" className="space-y-6">
          {/* Expiring Warranties */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-orange-500" />
                Garanci që Skadojnë (30 ditë)
                <Badge variant="warning" className="ml-2 bg-orange-100 text-orange-800">
                  {expiringWarranties.length}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {expiringWarranties.length === 0 ? (
                <p className="text-slate-500 text-center py-4">Nuk ka garanci që skadojnë së shpejti</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Produkti</TableHead>
                      <TableHead>Klienti</TableHead>
                      <TableHead>Kontakti</TableHead>
                      <TableHead>Skadon</TableHead>
                      <TableHead>Ditë</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {expiringWarranties.map((warranty) => (
                      <TableRow key={warranty.id}>
                        <TableCell className="font-medium">{warranty.product_name}</TableCell>
                        <TableCell>{warranty.customer_name}</TableCell>
                        <TableCell>
                          {warranty.customer_phone && (
                            <a href={`tel:${warranty.customer_phone}`} className="flex items-center gap-1 text-primary hover:underline">
                              <Phone className="w-3 h-3" />
                              {warranty.customer_phone}
                            </a>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-sm">{formatDate(warranty.end_date)}</TableCell>
                        <TableCell>
                          <Badge variant={warranty.days_until_expiry <= 7 ? 'destructive' : 'warning'}>
                            {warranty.days_until_expiry} ditë
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Low Stock */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5 text-rose-500" />
                Stok i Ulët
                <Badge variant="destructive" className="ml-2">
                  {lowStockProducts.length}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {lowStockProducts.length === 0 ? (
                <p className="text-slate-500 text-center py-4">Të gjitha produktet kanë stok të mjaftueshëm</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Produkti</TableHead>
                      <TableHead>Barcode</TableHead>
                      <TableHead className="text-center">Stoku</TableHead>
                      <TableHead className="text-center">Min</TableHead>
                      <TableHead>Statusi</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lowStockProducts.map((product) => (
                      <TableRow key={product.id}>
                        <TableCell className="font-medium">{product.name}</TableCell>
                        <TableCell className="font-mono text-sm">{product.barcode}</TableCell>
                        <TableCell className="text-center">
                          <Badge variant={product.stock <= 0 ? 'destructive' : 'warning'}>
                            {product.stock}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center font-mono">{product.min_stock}</TableCell>
                        <TableCell>
                          {product.stock <= 0 ? (
                            <span className="text-rose-600 font-medium">PA STOK</span>
                          ) : (
                            <span className="text-orange-600">Stok i ulët</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Daily Reports */}
        <TabsContent value="daily-reports">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Raport Ditor
              </CardTitle>
              <CardDescription>Gjenero raportin e ditës për printim ose arkivim</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4 items-end">
                <div>
                  <Label>Zgjidh Datën</Label>
                  <Input
                    type="date"
                    value={reportDate}
                    onChange={(e) => setReportDate(e.target.value)}
                    className="w-48"
                  />
                </div>
                <Button onClick={loadDailyReport}>
                  Gjenero Raportin
                </Button>
              </div>

              {dailyReport && (
                <div className="mt-6 space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="bg-primary/5">
                      <CardContent className="py-4 text-center">
                        <p className="text-2xl font-bold font-mono text-primary">
                          {formatCurrency(dailyReport.summary.total_sales)}
                        </p>
                        <p className="text-sm text-slate-500">Shitje Totale</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-lime-50">
                      <CardContent className="py-4 text-center">
                        <p className="text-2xl font-bold font-mono text-lime-600">
                          {formatCurrency(dailyReport.summary.total_cash_received)}
                        </p>
                        <p className="text-sm text-slate-500">Para Arkëtuara</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-orange-50">
                      <CardContent className="py-4 text-center">
                        <p className="text-2xl font-bold font-mono text-orange-600">
                          {formatCurrency(dailyReport.summary.total_purchases)}
                        </p>
                        <p className="text-sm text-slate-500">Blerje</p>
                      </CardContent>
                    </Card>
                    <Card className={dailyReport.summary.net_cash_flow >= 0 ? 'bg-lime-100' : 'bg-rose-100'}>
                      <CardContent className="py-4 text-center">
                        <p className={`text-2xl font-bold font-mono ${dailyReport.summary.net_cash_flow >= 0 ? 'text-lime-700' : 'text-rose-700'}`}>
                          {formatCurrency(dailyReport.summary.net_cash_flow)}
                        </p>
                        <p className="text-sm text-slate-500">Fluksi Neto</p>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="flex gap-2">
                    <Badge variant="secondary">{dailyReport.counts.sales_count} shitje</Badge>
                    <Badge variant="secondary">{dailyReport.counts.purchases_count} blerje</Badge>
                    <Badge variant="secondary">{dailyReport.counts.services_completed} servise</Badge>
                  </div>

                  <Button onClick={printDailyReport}>
                    <Printer className="w-4 h-4 mr-2" />
                    Printo Raportin
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Backup */}
        <TabsContent value="backup-restore">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Download className="w-5 h-5" />
                Backup & Restore
              </CardTitle>
              <CardDescription>Eksporto ose importo të dhënat e sistemit</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex gap-4">
                <Button onClick={exportBackup} className="flex-1 h-20">
                  <Download className="w-6 h-6 mr-3" />
                  <div className="text-left">
                    <p className="font-medium">Eksporto Backup</p>
                    <p className="text-xs opacity-80">Shkarko të gjitha të dhënat</p>
                  </div>
                </Button>
                <Button variant="outline" onClick={() => setIsBackupModalOpen(true)} className="flex-1 h-20">
                  <Upload className="w-6 h-6 mr-3" />
                  <div className="text-left">
                    <p className="font-medium">Importo Backup</p>
                    <p className="text-xs opacity-80">Rikthe të dhënat nga skedar</p>
                  </div>
                </Button>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-yellow-800">Kujdes!</p>
                    <p className="text-sm text-yellow-700">
                      Importimi i backup-it do të mbishkruajë të gjitha të dhënat ekzistuese.
                      Sigurohuni që keni eksportuar një backup të ri para se të importoni.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Import Backup Modal */}
      <Dialog open={isBackupModalOpen} onOpenChange={setIsBackupModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Importo Backup</DialogTitle>
            <DialogDescription>
              Zgjidhni skedarin JSON të eksportuar më parë
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              type="file"
              accept=".json"
              onChange={(e) => setBackupFile(e.target.files?.[0] || null)}
            />
            {backupFile && (
              <p className="text-sm text-slate-500">
                Skedari: {backupFile.name} ({(backupFile.size / 1024).toFixed(1)} KB)
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBackupModalOpen(false)}>
              Anulo
            </Button>
            <Button onClick={importBackup} disabled={!backupFile}>
              Importo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
